import React, { useEffect } from "react";
import $ from "jquery";
import { IconInfo } from "../icons";
import Tooltip from "../tooltip";

const HtmlSelect = (props) => {
    const {
        id,
        name,
        className,
        value,
        readOnly,
        placeholder,
        options,
        ...other
    } = props;

    return (
        <select
            id={id ?? name ?? ""}
            name={name ?? ""}
            className={className}
            readOnly={readOnly}
            placeholder={placeholder}
            value={value}
            {...other}
        >
            {options.map((opt, index) => {
                return (
                    <option key={index} value={opt.value}>
                        {opt.text}
                    </option>
                );
            })}
        </select>
    );
};

const PrefixSign = ({ text, error }) => {
    if (!text) return null;
    return (
        <div
            className={`absolute bg-gray-50 rounded-y-md rounded-l-md py-4 text-center br-1 border-gray-300 ${
                error ? "text-skin-error" : ""
            }`}
            style={{
                top: error ? "2px" : "1px",
                left: "2px",
                width: "39px",
                borderRightWidth: "1px",
            }}
        >
            {text}
        </div>
    );
};

const Input = (props) => {
    const {
        id,
        name,
        type,
        className,
        label,
        error,
        note,
        readOnly,
        tooltip,
        placeholder,
        prefixSign,
        regex,
        regexError,
        options,
        ...other
    } = props;

    const css = `rounded-md border-gray-300 focus:border-skin-primary focus:ring-skin-primary outline-none focus:ring-0
        placeholder:text-slate-400 w-full
        ${readOnly ? "bg-slate-50 text-slate-500 border-slate-200" : ""}
        ${className ?? ""}
        ${
            error
                ? "text-skin-error border-2 border-skin-error placeholder:text-skin-error focus:border-skin-error"
                : ""
        }`;
    const labelCss = `${error ? "text-skin-error" : ""}`;
    const tooltipCss = `w-10 h-4 ${
        error ? "text-skin-error" : "text-slate-400"
    }`;

    const useLabelTooltip = tooltip && label?.length > 0;
    const usePlaceholderTooltip = tooltip && placeholder && !useLabelTooltip;

    useEffect(() => {
        if (tooltip && tooltip.length > 0) {
            $('[data-toggle="tooltip"]').tooltip({
                trigger: "hover",
            });
        }
    }, [tooltip]);

    let typeValue = "text";
    if (!type || type === "Text" || type === "Number") {
        typeValue = type.toLowerCase();
    }

    return (
        <div className="flex flex-col space-y-1">
            <div className="flex items-center gap-2">
                {label && label.length > 0 && (
                    <label htmlFor={id ?? name} className={labelCss}>
                        {label}
                    </label>
                )}
                {useLabelTooltip && (
                    <Tooltip content={tooltip}>
                        <IconInfo className="h-4 w-4" />
                    </Tooltip>
                )}
            </div>
            <div className="relative">
                <PrefixSign error={error} text={prefixSign} />
                {type === "Select" ? (
                    <HtmlSelect
                        id={id ?? name ?? ""}
                        name={name ?? ""}
                        type={typeValue ?? "text"}
                        className={css}
                        readOnly={readOnly}
                        placeholder={placeholder}
                        options={options}
                        {...other}
                    />
                ) : (
                    <input
                        id={id ?? name ?? ""}
                        name={name ?? ""}
                        type={typeValue ?? "text"}
                        className={css}
                        readOnly={readOnly}
                        placeholder={placeholder}
                        style={prefixSign ? { paddingLeft: "56px" } : null}
                        {...other}
                    />
                )}

                {usePlaceholderTooltip && (
                    <div className="flex items-center absolute inset-y-0 right-0">
                        <Tooltip content={tooltip}>
                            <IconInfo className={tooltipCss} />
                        </Tooltip>
                    </div>
                )}
            </div>
            {error && error.length > 0 && (
                <div
                    data-error-for={id ?? name}
                    className="text-skin-error text-xs"
                >
                    {error}
                </div>
            )}
            {note && note.length > 0 && (
                <div
                    data-note-for={id ?? name}
                    className="text-xs text-skin-light"
                >
                    {note}
                </div>
            )}
        </div>
    );
};

export const Checkbox = (props) => {
    const { id, name, className, label, error, note, tooltip, ...other } =
        props;
    const css = `rounded-sm border-gray-300 text-skin-primary focus:border-skin-primary focus:ring-skin-primary
        ${className ?? ""}
        ${
            error
                ? "text-skin-error border-2 border-skin-error placeholder:text-skin-error"
                : ""
        }`;

    const labelCss = `${error ? "text-skin-error" : ""}`;

    useEffect(() => {
        if (tooltip && tooltip.length > 0) {
            $('[data-toggle="tooltip"]').tooltip({
                trigger: "hover",
            });
        }
    }, [tooltip]);

    return (
        <div>
            <div className="flex space-x-2 items-center">
                <input
                    type="checkbox"
                    id={id ?? name ?? ""}
                    name={name ?? ""}
                    className={css}
                    {...other}
                />
                <label className={labelCss}>{props.label}</label>
                {/* TODO: Discuss with the team about csn-theme */}
                {tooltip && tooltip.length > 0 && (
                    <Tooltip content={tooltip}>
                        <IconInfo className="h-4 w-4" />
                    </Tooltip>
                )}
            </div>
            {error && error.length > 0 && (
                <div className="font-normal text-skin-error text-xs">
                    {error}
                </div>
            )}
            {note && note.length > 0 && (
                <div className="text-xs text-skin-light">{note}</div>
            )}
        </div>
    );
};

export default Input;
